import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { FaCalendarAlt, FaChevronDown, FaCheck } from "react-icons/fa";
import Clickable from "./Clickable";
import IsoDateInput from "./IsoDateInput";
import { PRESETS, DEFAULT_RANGE, dateRangeLabel } from "./dateRanges";

/**
 * Nút lọc khoảng ngày — cùng dáng FilterDropdown. Popover: danh sách preset bên
 * trên, khoảng tự chọn (From/To) bên dưới.
 *
 * value    = { range, from, to } đã resolve (xem dateRanges.resolveDateRange)
 * onChange = ({ range }) cho preset, ({ from, to }) cho khoảng tự chọn
 */
const POPOVER_WIDTH = 280;

const DateRangeFilter = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [draft, setDraft] = useState({ from: value.from, to: value.to });
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);

  // Không phải mặc định → tô như filter đang bật.
  const active = value.range !== DEFAULT_RANGE;

  useEffect(() => { if (open) setDraft({ from: value.from, to: value.to }); }, [open, value.from, value.to]);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return undefined;
    const compute = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      const left = rect.left + POPOVER_WIDTH > window.innerWidth - 8 ? Math.max(8, rect.right - POPOVER_WIDTH) : rect.left;
      setCoords({ top: rect.bottom + 6, left });
    };
    compute();
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => { window.removeEventListener("scroll", compute, true); window.removeEventListener("resize", compute); };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      // Lịch của DatePicker render ra <body> (portal) — click trong đó không phải "click ra ngoài".
      if (e.target.closest?.(".react-datepicker, .react-datepicker-popper")) return;
      if (!buttonRef.current?.contains(e.target) && !popoverRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const pick = (range) => { onChange({ range }); setOpen(false); };
  const invalid = Boolean(draft.from && draft.to && draft.from > draft.to);
  const canApply = (draft.from || draft.to) && !invalid;
  const apply = () => { if (canApply) { onChange({ from: draft.from, to: draft.to }); setOpen(false); } };

  const popover = open && coords && createPortal(
    <div ref={popoverRef} role="dialog" aria-label="Date range"
      style={{ position: "fixed", top: coords.top, left: coords.left, width: POPOVER_WIDTH, zIndex: 100000 }}
      className="geist-scope tw-bg-geist-bg tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-md tw-shadow-geist-menu tw-overflow-hidden">
      <div className="tw-py-1.5">
        {PRESETS.map((p) => {
          const checked = value.range === p.key;
          return (
            <Clickable key={p.key} onClick={() => pick(p.key)}
              className={`tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-text-sm tw-transition-colors ${
                checked ? "tw-bg-geist-100 tw-text-geist-fg tw-font-medium" : "tw-text-geist-fg-2 hover:tw-bg-geist-100"
              }`}>
              <span>{p.label}{p.key === DEFAULT_RANGE && <span className="tw-ml-1.5 tw-text-[10px] tw-text-geist-fg-3">default</span>}</span>
              {checked && <FaCheck size={10} className="tw-text-geist-accent" />}
            </Clickable>
          );
        })}
      </div>

      <div className="tw-px-3 tw-py-3 tw-border-0 tw-border-t tw-border-solid tw-border-geist-border tw-bg-geist-bg-2">
        <div className="tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-geist-fg-3 tw-mb-2">Custom range</div>
        <div className="tw-grid tw-grid-cols-2 tw-gap-2">
          <IsoDateInput aria-label="Created from" value={draft.from} onChange={(from) => setDraft((d) => ({ ...d, from }))} />
          <IsoDateInput aria-label="Created to" value={draft.to} onChange={(to) => setDraft((d) => ({ ...d, to }))} />
        </div>
        {invalid && <p className="tw-text-[11px] tw-text-geist-error tw-m-0 tw-mt-1.5">“From” must be on or before “To”.</p>}
        <Clickable onClick={apply} disabled={!canApply}
          className={`tw-mt-2.5 tw-flex tw-items-center tw-justify-center tw-h-8 tw-rounded-geist-sm tw-text-[12px] tw-font-semibold ${
            canApply ? "tw-bg-geist-accent tw-text-white hover:tw-bg-geist-accent-2" : "tw-bg-geist-100 tw-text-geist-fg-3"
          }`}>
          Apply
        </Clickable>
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <Clickable ref={buttonRef} onClick={() => setOpen((o) => !o)} title="Created date"
        className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-py-1.5 tw-px-3 tw-text-xs tw-rounded-geist-sm tw-border tw-border-solid tw-transition-colors ${
          active ? "tw-border-geist-accent tw-bg-geist-accent/5 tw-text-geist-accent tw-font-semibold" : "tw-border-geist-border tw-bg-geist-bg tw-text-geist-fg-2 hover:tw-bg-geist-100"
        }`}>
        <FaCalendarAlt size={11} />
        <span className="tw-max-w-[200px] tw-truncate">{dateRangeLabel(value)}</span>
        <FaChevronDown size={9} className="tw-text-geist-fg-3" />
      </Clickable>
      {popover}
    </>
  );
};

DateRangeFilter.propTypes = {
  value: PropTypes.shape({
    range: PropTypes.string,
    from: PropTypes.string,
    to: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default DateRangeFilter;
